import React, { useEffect, useState } from "react";
import { Card, Chip, Empty, ErrorState, Field, PageHeading, Person, Pill, RowsSkeleton, SearchInput, Select, Table, Toggle, Workspace } from "../ui";
import { Icon } from "../icons";
import { useFilters } from "../filters";
import { personLabel, useView } from "../data";
import { CHANNEL_WORDS, ENTITY_LABELS, FAILURE_LABELS, RUN_STATUS, ago, fmt, words } from "../format";
import { MIN_REQUESTS, Toolbar, countActive } from "./lists";

const PAGE = 50;

/** Text typed into a search box, settled — so the server is not re-queried on every key. */
function useSettled(value, delay = 350) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return settled;
}

/**
 * Every conversation in the period, filtered on the server. The page's
 * workspace, person, date and request-status filters apply here; the list's
 * own filters live in the URL beside them (`c.*`).
 */
/** The judge's verdict on a turn in the conversation (`/obs/v1/sessions?judged`). */
const JUDGED_OPTIONS = [
  ["", "Any"],
  ["not_done", "Fell short"],
  ["partial", "Did part of it"],
  ["failure", "Did not do it"],
  ["success", "Did what they asked"],
];

export default function Conversations() {
  const { params, f: global, set, conv: c, setConv } = useFilters();
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState(c.q ?? "");
  const settledQ = useSettled(q);
  useEffect(() => setConv({ q: settledQ.trim() || null }), [settledQ]);

  const query = {
    limit: PAGE,
    offset,
    ...(global.status ? { status: global.status } : {}),
    ...(global.person ? { userId: global.person } : {}),
    ...(c.q ? { q: c.q } : {}),
    ...(c.lastStatus ? { lastStatus: c.lastStatus } : {}),
    ...(c.failed ? { hasFailures: "true" } : {}),
    ...(c.feedback ? { feedback: c.feedback } : {}),
    ...(c.channel ? { channel: c.channel } : {}),
    ...(c.originPage ? { originPage: c.originPage } : {}),
    ...(c.source ? { source: c.source } : {}),
    ...(c.minRequests ? { minRequests: c.minRequests } : {}),
    ...(c.judged ? { judged: c.judged } : {}),
    ...(c.why ? { why: c.why } : {}),
    ...(c.created ? { created: c.created } : {}),
    // A number on the Agent models tab: which one (`model`) and its labels (`d.*`).
    ...(c.model ? { model: c.model, ...modelDetails(c.modelOf) } : {}),
    sort: c.sort || "recent",
  };
  const filterKey = JSON.stringify({ ...query, offset: 0, params });
  useEffect(() => setOffset(0), [filterKey]);
  const { data, loading, refreshing, error } = useView("sessions", params, query);
  const facets = data?.facets ?? { channels: [], originPages: [], sources: [], people: [] };
  const total = data?.total ?? null;
  const sessions = data?.sessions ?? [];
  const { sort: _sort, modelOf: _of, modelTitle: _title, ...counted } = c;
  const clearModel = () => setConv({ model: null, modelOf: null, modelTitle: null });

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <PageHeading title="Conversations">Every conversation in this period. Open one to read it, with its scores, cost and full trace.</PageHeading>

      <Toolbar shown={total == null ? null : Math.min(total, offset + sessions.length)} total={total} noun="conversations" active={countActive(counted)} onClear={() => {
        setQ("");
        setConv({ q: null, lastStatus: null, failed: null, feedback: null, channel: null, originPage: null, source: null, minRequests: null, judged: null, why: null, created: null, model: null, modelOf: null, modelTitle: null, sort: null });
      }}>
        {c.model ? (
          <Field label="Agent model">
            <Chip k="Model" v={c.modelTitle || words(c.model)} title="From the Agent models tab" onRemove={clearModel} />
          </Field>
        ) : null}
        <Field label="Search">
          <SearchInput value={q} onChange={setQ} placeholder="Words they used, or a conversation id" width={280} />
        </Field>
        <Field label="Person">
          <Select
            value={global.person ?? ""}
            onChange={(v) => set({ person: v || null })}
            options={[["", "Everyone"], ...facets.people.map((id) => [id, personLabel(id)]).sort((a, b) => a[1].localeCompare(b[1]))]}
            width={180}
          />
        </Field>
        <Field label="Latest request">
          <Select value={c.lastStatus ?? ""} onChange={(v) => setConv({ lastStatus: v })} options={[["", "Any"], ["finished", "Finished"], ["open", "Waiting for user"], ["stopped", "Stopped by user"], ["failed", "Failed"]]} />
        </Field>
        <Field label="Reviewed as">
          <Select value={c.judged ?? ""} onChange={(v) => setConv({ judged: v, ...(v ? {} : { why: null }) })} options={JUDGED_OPTIONS} />
        </Field>
        {c.judged && (
          <Field label="Why">
            <Select value={c.why ?? ""} onChange={(v) => setConv({ why: v })} options={[["", "Any reason"], ...Object.entries(FAILURE_LABELS)]} />
          </Field>
        )}
        <Field label="Created">
          <Select value={c.created ?? ""} onChange={(v) => setConv({ created: v })} options={[["", "Anything or nothing"], ...Object.entries(ENTITY_LABELS).map(([k, v]) => [k, `A ${v.one.toLowerCase()}`])]} />
        </Field>
        <Field label="Feedback">
          <Select value={c.feedback ?? ""} onChange={(v) => setConv({ feedback: v })} options={[["", "Any"], ["any", "Rated"], ["negative", "Rated badly"], ["positive", "Rated well"], ["none", "Not rated"]]} />
        </Field>
        {facets.channels.length > 1 && (
          <Field label="Where">
            <Select value={c.channel ?? ""} onChange={(v) => setConv({ channel: v })} options={[["", "Anywhere"], ...facets.channels.map((v) => [v, CHANNEL_WORDS[v] ?? words(v)])]} />
          </Field>
        )}
        {facets.originPages.length > 0 && (
          <Field label="Started from">
            <Select value={c.originPage ?? ""} onChange={(v) => setConv({ originPage: v })} options={[["", "Any page"], ...facets.originPages.map((v) => [v, `${words(v)} page`])]} />
          </Field>
        )}
        {facets.sources.length > 1 && (
          <Field label="Search source">
            <Select value={c.source ?? ""} onChange={(v) => setConv({ source: v })} options={[["", "Any"], ...facets.sources.map((v) => [v, facets.sourceLabels?.[v] ?? words(v)])]} />
          </Field>
        )}
        <Field label="Requests">
          <Select value={c.minRequests ?? ""} onChange={(v) => setConv({ minRequests: v })} options={MIN_REQUESTS} />
        </Field>
        <Field label="Sort by">
          <Select value={c.sort ?? "recent"} onChange={(v) => setConv({ sort: v === "recent" ? null : v })} options={[["recent", "Most recent"], ["oldest", "Oldest first"], ["requests", "Most requests"], ["messages", "Most messages"]]} />
        </Field>
        <Field label="Only">
          <Toggle on={Boolean(c.failed)} onChange={(v) => setConv({ failed: v ? "1" : null })}>
            Had a failed request
          </Toggle>
        </Field>
      </Toolbar>

      <Card
        flush
        title="Conversations"
        subtitle={refreshing ? "Updating…" : "Click a row to read the conversation"}
        actions={
          <>
            <span className="aa-num" style={{ fontSize: 12, color: "var(--aa-muted)" }}>
              {total ? `${fmt(offset + 1)}–${fmt(Math.min(total, offset + PAGE))} of ${fmt(total)}` : null}
            </span>
            <button type="button" className="aa-btn aa-btn-sm aa-btn-icon" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} aria-label="Newer">
              <Icon name="chevronLeft" size={14} />
            </button>
            <button type="button" className="aa-btn aa-btn-sm aa-btn-icon" disabled={total == null || offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)} aria-label="Older">
              <Icon name="chevronRight" size={14} />
            </button>
          </>
        }
      >
        {loading && !data ? (
          <RowsSkeleton rows={8} />
        ) : error ? (
          <ErrorState error={error} />
        ) : sessions.length ? (
          <div style={{ opacity: refreshing ? 0.55 : 1, transition: "opacity .15s" }}>
            <Table
              onRow={(r) => set({ session: r.id })}
              columns={[
                {
                  key: "first_prompt",
                  label: "What they asked first",
                  max: 420,
                  sortable: false,
                  render: (r) => (
                    <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", fontWeight: 500 }}>
                      {r.title?.replace(/\*\*/g, "") || r.first_prompt || <span style={{ color: "var(--aa-faint)" }}>(no text)</span>}
                    </span>
                  ),
                },
                { key: "account_id", label: "Workspace", sortable: false, render: (r) => <Workspace id={r.account_id} /> },
                { key: "user_id", label: "Person", sortable: false, render: (r) => <Person id={r.user_id} compact /> },
                { key: "run_count", label: "Requests", align: "right", sortable: false, render: (r) => fmt(r.run_count) },
                {
                  key: "last_status",
                  label: "Latest request",
                  sortable: false,
                  render: (r) => {
                    const s = RUN_STATUS[r.last_status];
                    return <Pill tone={s?.tone ?? "neutral"} dot>{s?.label ?? words(r.last_status)}</Pill>;
                  },
                },
                { key: "failed_runs", label: "Failed", align: "right", sortable: false, render: (r) => (r.failed_runs ? <span style={{ color: "var(--aa-bad-ink)", fontWeight: 600 }}>{r.failed_runs}</span> : <span style={{ color: "var(--aa-faint)" }}>0</span>) },
                { key: "thumbs", label: "👍 / 👎", align: "right", sortable: false, render: (r) => `${r.thumbs_up} / ${r.thumbs_down}` },
                { key: "last_activity_at", label: "Last active", nowrap: true, sortable: false, render: (r) => <span title={new Date(r.last_activity_at).toLocaleString()}>{ago(r.last_activity_at)}</span> },
              ]}
              rows={sessions}
            />
          </div>
        ) : (
          <Empty icon="conversations" title="No conversation matches these filters">
            Widen the date range or clear a filter above.
          </Empty>
        )}
      </Card>
    </div>
  );
}

/** `c.modelOf` ("from=prospeo&to=perplexity") as the API's `d.*` labels. */
function modelDetails(of) {
  if (!of) return {};
  return Object.fromEntries([...new URLSearchParams(of).entries()].map(([k, v]) => [`d.${k}`, v]));
}
